import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import UndoIcon from "@mui/icons-material/Undo";

const PAGE_SIZE = 10;
const getVisibleStatus = (emp = {}, selectedIds = new Set()) => {
  const isCompletedAttempts = emp.assessmentStatus === "Completed Attempts";
  const isAlreadyAdded = emp.isAlreadyAdded || isCompletedAttempts;

  if (emp.isAssignedElsewhere) return "Already Assigned";
  if (isAlreadyAdded) {
    if (isCompletedAttempts) return "Completed Attempts";
    if (emp.isActive) return "Active in Table";
    return "Completed";
  }
  if (emp.wasPreviouslyRemoved) return "Deleted (Available)";
  if (selectedIds.has(emp.GUID)) return "Selected";
  return emp.assessmentStatus || "Available";
};

const getStatusSearchAlias = (emp = {}) => {
  if (emp.assessmentStatus === "Pending Assessment") return "Pending Attempts";
  return "";
};

const EmployeeLookupDialog = ({
  open,
  onClose,
  examId,
  selectedEmployees,
  setSelectedEmployees,
  onAddEmployees,
  existingTableEmployees = [],
  onUndoEmployee,
}) => {
  const [allRows, setAllRows] = useState([]);
  const [displayRows, setDisplayRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState("");

  // NEW: State for all departments (static, fetched once)
  const [allDepartments, setAllDepartments] = useState([]);

  const requestIdRef = useRef(0);

  const activeEmployeeIds = useMemo(() => {
    return new Set(
      (existingTableEmployees || [])
        .filter((e) => e.isActive === true)
        .map((e) => e.GUID)
    );
  }, [existingTableEmployees]);

  const allTableEmployeeIds = useMemo(() => {
    return new Set((existingTableEmployees || []).map((e) => e.GUID));
  }, [existingTableEmployees]);

  const inactiveEmployeeIds = useMemo(() => {
    return new Set(
      (existingTableEmployees || [])
        .filter((e) => e.isActive === false)
        .map((e) => e.GUID)
    );
  }, [existingTableEmployees]);

  const selectedIds = useMemo(() => {
    return new Set((selectedEmployees || []).map((e) => e.GUID));
  }, [selectedEmployees]);

  // NEW: Fetch all departments once when dialog opens
  const fetchAllDepartments = useCallback(async () => {
    if (!examId) return;

    try {
      const res = await axios.get("/coordinators/departments-list/", {
        params: { exam_id: examId }
      });

      if (res.data?.success) {
        const depts = (res.data.data || []).map(dept => ({
          id: dept.DepartmentGUID,
          name: dept.Name
        }));
        setAllDepartments(depts);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  }, [examId]);

  // Fetch employees with filters
  const fetchEmployees = useCallback(async (deptIds = [], search = "") => {
    if (!examId) return;

    const requestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const res = await axios.post(
        "/coordinators/employees-list/",
        {},
        {
          params: {
            exam_id: examId,
            department_ids: deptIds.join(","),
            search: "",
            page: 1,
            page_size: 1000,
          },
        }
      );

      if (requestId !== requestIdRef.current) return;

      const employeesData = res.data?.data || [];

      const normalized = employeesData.map((emp, index) => {
        const isAssignedElsewhere = Boolean(
          emp.IsAlreadyAssigned ?? emp.isAlreadyAssigned ?? emp.AlreadyAssigned
        );
        return {
          id: emp.GUID ?? `${emp.EmployeeId}-${index}`,
          GUID: emp.GUID,
          employeeId: emp.EmployeeId,
          employeeName: emp.EmployeeName,
          departmentId: emp.DepartmentId,
          departmentName: emp.DepartmentName,
          assessmentStatus: emp.AssessmentStatus || "Available",
          userAttemptCount: emp.UserAttemptCount ?? emp.userAttemptCount ?? 0,
          attemptCount: emp.AttemptCount ?? emp.attemptCount ?? 1,
          isAssignedElsewhere,
          isAlreadyAdded: activeEmployeeIds.has(emp.GUID) || isAssignedElsewhere,
          wasPreviouslyRemoved: inactiveEmployeeIds.has(emp.GUID),
          isActive: activeEmployeeIds.has(emp.GUID),
        };
      });

      setAllRows(normalized);
      setPage(0);
    } catch (error) {
      console.error("Fetch error:", error);
      setAllRows([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [examId, activeEmployeeIds, inactiveEmployeeIds]);

  // Fetch departments when dialog opens
  useEffect(() => {
    if (open) {
      fetchAllDepartments();
    }
  }, [open, fetchAllDepartments]);

  // Fetch employees when filters change
  useEffect(() => {
    if (open) {
      const deptIds = departmentFilter ? [departmentFilter] : [];
      fetchEmployees(deptIds, searchTerm);
    }
  }, [open, departmentFilter, searchTerm, fetchEmployees]);

  // Reset page when filter changes
  useEffect(() => {
    setPage(0);
  }, [departmentFilter, searchTerm]);

  // Reset search, department, and page when dialog opens
  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setDepartmentFilter("");
      setPage(0);
    }
  }, [open]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return allRows;

    return allRows.filter((emp) => {
      const visibleStatus = getVisibleStatus(emp, selectedIds);
      return [
        emp.employeeName,
        emp.employeeId,
        emp.departmentName,
        emp.assessmentStatus,
        visibleStatus,
        getStatusSearchAlias(emp),
      ].some((value) =>
        String(value || "").toLowerCase().includes(normalizedSearch)
      );
    });
  }, [allRows, searchTerm, selectedIds]);

  // Pagination slice
  useEffect(() => {
    const start = page * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    setDisplayRows(filteredRows.slice(start, end));
  }, [filteredRows, page]);

  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE) || 1;

  const handlePageChange = (newPage) => {
    if (newPage < 0 || newPage >= totalPages) return;
    setPage(newPage);
  };

  const isAllSelected = useMemo(() => {
    // if (displayRows.length === 0) return false;
    // const selectableRows = displayRows.filter((emp) => !emp.isAlreadyAdded);
    // if (selectableRows.length === 0) return false;
    // return selectableRows.every((emp) => selectedIds.has(emp.GUID));
    const selectableRows = filteredRows.filter(
      emp => !emp.isAlreadyAdded
    );

    if (selectableRows.length === 0) return false;

    return selectableRows.every(emp => selectedIds.has(emp.GUID));
  }, [filteredRows, selectedIds]);

  const isIndeterminate = useMemo(() => {
    // if (displayRows.length === 0) return false;
    // const selectableRows = displayRows.filter((emp) => !emp.isAlreadyAdded);
    // if (selectableRows.length === 0) return false;
    // const selectedCount = selectableRows.filter((emp) => selectedIds.has(emp.GUID)).length;
    // return selectedCount > 0 && selectedCount < selectableRows.length;
    const selectableRows = filteredRows.filter(
      emp => !emp.isAlreadyAdded
    );

    if (selectableRows.length === 0) return false;

    const selectedCount = selectableRows.filter(
      emp => selectedIds.has(emp.GUID)
    ).length;

    return (
      selectedCount > 0 &&
      selectedCount < selectableRows.length
    );
  }, [filteredRows, selectedIds]);

  // const handleSelectAllOnPage = () => {
  //   const selectableRows = displayRows.filter((emp) => !emp.isAlreadyAdded);

  //   if (isAllSelectedOnPage) {
  //     const currentGuids = new Set(selectableRows.map((emp) => emp.GUID));
  //     setSelectedEmployees((prev) =>
  //       prev.filter((emp) => !currentGuids.has(emp.GUID))
  //     );
  //   } else {
  //     const newEmployees = selectableRows.filter((emp) => !selectedIds.has(emp.GUID));
  //     setSelectedEmployees((prev) => [...prev, ...newEmployees]);
  //   }
  // };
  const handleSelectAll = () => {

    const selectableRows = filteredRows.filter(
      emp => !emp.isAlreadyAdded
    );

    if (isAllSelected) {

      const guids = new Set(
        selectableRows.map(emp => emp.GUID)
      );

      setSelectedEmployees(prev =>
        prev.filter(emp => !guids.has(emp.GUID))
      );

    } else {

      const newEmployees = selectableRows.filter(
        emp => !selectedIds.has(emp.GUID)
      );

      setSelectedEmployees(prev => [
        ...prev,
        ...newEmployees,
      ]);

    }
  };

  const toggleEmployee = (employee) => {
    if (employee.isAlreadyAdded) return;

    setSelectedEmployees((prev) => {
      const exists = prev.some((e) => e.GUID === employee.GUID);
      return exists
        ? prev.filter((e) => e.GUID !== employee.GUID)
        : [...prev, employee];
    });
  };

  const handleAddEmployees = () => {
    const newEmployees = selectedEmployees.filter(
      (emp) => !activeEmployeeIds.has(emp.GUID)
    );
    onAddEmployees(newEmployees);
    setSelectedEmployees([]);
    onClose();
  };

  const handleDepartmentChange = (event) => {
    setDepartmentFilter(event.target.value);
    setSearchTerm("");
    setSelectedEmployees([]);
    setPage(0);
  };

  const handleClose = () => {
    setSelectedEmployees([]);
    onClose();
  };

  const renderPagination = () => {
    const currentPage = page + 1;

    const renderPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;

      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
      } else if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
      }
      return pages;
    };

    return (
      <div className="pd-pagination" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", margin: "20px 0" }}>
        <button
          className="btn-negative"
          disabled={currentPage === 1}
          onClick={() => handlePageChange(page - 1)}
        >
          Previous
        </button>

        <div className="pd-page-numbers" style={{ display: "flex", gap: "6px" }}>
          {renderPageNumbers().map((p, idx) => (
            <button
              key={idx}
              disabled={p === "..."}
              onClick={() => typeof p === "number" && handlePageChange(p - 1)}
              className={`pg-type-btn ${p === currentPage ? "active" : ""}`}
            >
              {p}
            </button>
          ))}
        </div>

        <button
          className="btn-negative"
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(page + 1)}
        >
          Next
        </button>
      </div>
    );
  };

  const newEmployeesCount = selectedEmployees.filter(
    (emp) => !activeEmployeeIds.has(emp.GUID)
  ).length;

  const inactiveCount = (existingTableEmployees || []).filter(
    (e) => e.isActive === false
  ).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Select Employees
        <IconButton onClick={handleClose} sx={{ position: "absolute", right: 10, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Search + Department */}
        <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search by Employee Name, Employee Code,  Department."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: "60%" }}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "gray" }} /> }}
          />

          <TextField
            select
            size="small"
            value={departmentFilter}
            onChange={handleDepartmentChange}
            sx={{ width: "40%" }}
            SelectProps={{ native: true }}
          >
            <option value="">All Departments</option>
            {allDepartments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </TextField>
        </Box>
        <Box>
          <table
            className="pd-table"
            style={{
              width: "100%",
              borderSpacing: "0 0.15rem",
              tableLayout: "auto",
            }}
          >

            <thead>
              <tr>
                <th style={{ textAlign: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ fontSize: "14px", fontWeight: 500 }}>
                      Select All
                    </span>

                    <Checkbox
                      sx={{
                        color: "inherit",
                        "&.Mui-checked": {
                          color: "white",
                        },
                      }}
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                    />
                  </div>
                </th>

                <th style={{ textAlign: "center" }}>
                  Employee Name
                </th>

                <th style={{ textAlign: "center" }}>
                  Employee Code
                </th>

                <th style={{ textAlign: "center" }}>
                  Department
                </th>

                <th style={{ textAlign: "center" }}>
                  Status
                </th>
              </tr>
            </thead>


            <tbody>

              {loading ? (

                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: "center",
                      padding: "50px",
                      verticalAlign: "middle",
                    }}
                  >
                    <span>
                      Loading employees...
                    </span>
                  </td>
                </tr>


              ) : displayRows.length === 0 ? (

                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: "center",
                      padding: "50px",
                      verticalAlign: "middle",
                    }}
                  >
                    No employees found
                  </td>
                </tr>


              ) : (

                displayRows.map((emp) => {
                  const isCompletedAttempts =
                   emp.assessmentStatus === "Completed Attempts";

                  const isAlreadyAdded = emp.isAlreadyAdded || isCompletedAttempts;
                  const isActive = emp.isActive;
                  const isSelected = selectedIds.has(emp.GUID);
                  const wasPreviouslyRemoved = emp.wasPreviouslyRemoved;
                  const isAssignedElsewhere = emp.isAssignedElsewhere;
                  

                  // let statusText = "Available";
                  let statusText = emp.assessmentStatus || "Available";


                  if (isAssignedElsewhere) {

                    statusText = "Already Assigned";

                  }
                  else if (isAlreadyAdded) {

                    if (isCompletedAttempts) {
                      statusText = "Completed Attempts";
                  }

                    if (isActive) {
                      statusText = "Active in Table";
                    }
                    else {
                      statusText = "Completed";
                    }

                  }
                  else if (wasPreviouslyRemoved) {

                    statusText = "Deleted (Available)";

                  }
                  else if (isSelected) {

                    statusText = "Selected";

                  }


                  return (

                    <tr
                      key={emp.GUID}
                      onClick={() =>
                        !isAlreadyAdded && toggleEmployee(emp)
                      }
                      style={{

                        cursor: isAlreadyAdded
                          ? "not-allowed"
                          : "pointer",

                        opacity: isAlreadyAdded ? 0.6 : 1,

                        backgroundColor:

                          isAlreadyAdded && !isActive
                            ? "#ffebee"

                            : isAlreadyAdded
                              ? "#f5f5f5"

                              : "transparent",
                      }}
                    >


                      <td
                        style={{
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                      >

                        <Checkbox

                          // sx={{
                          //   color: "inherit",

                          //   "&.Mui-checked": {
                          //     color: "white",
                          //   },
                          // }}
                          sx={{
                            color: "#333",
                            "&.Mui-checked": {
                              color: "#1976d2",
                            },
                          }}

                          checked={
                            isSelected || isAlreadyAdded
                          }

                          disabled={
                            isAlreadyAdded
                          }

                          onClick={(e) =>
                            e.stopPropagation()
                          }

                          onChange={() =>
                            !isAlreadyAdded &&
                            toggleEmployee(emp)
                          }

                        />

                      </td>



                      <td
                        style={{
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                      >

                        {emp.employeeName || "-"}

                      </td>




                      <td
                        style={{
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                      >

                        {emp.employeeId || "-"}

                      </td>




                      <td
                        style={{
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                      >

                        {emp.departmentName || "-"}

                      </td>




                      <td
                        style={{
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                      >

                        {/* <Chip

                          label={statusText}

                          size="small"

                          color={statusColor}

                          variant={
                            isAlreadyAdded
                              ? "filled"
                              : "outlined"
                          }

                          sx={{
                            "& .MuiChip-label": {
                              color: "inherit",
                            },
                          }}

                        /> */}

                        <Chip
                          label={statusText}
                          size="small"
                          variant="filled"
                          sx={{
                            minWidth: 130,
                            height: 34,
                            borderRadius: "18px",
                            fontWeight: 500,
                            fontSize: "14px",

                            ...(statusText === "Available" && {
                              backgroundColor: "#D8F0DE",
                              color: "#1B5E20",
                            }),

                            ...(statusText === "Completed Attempts" && {
                              backgroundColor: "#d62c2c",
                              color: "#cf2c2c",
                            }),

                            ...(statusText === "Pending Assessment" && {
                              backgroundColor: "#FFF4D6",
                              color: "#B26A00",
                            }),

                            ...(statusText === "Attempted" && {
                              backgroundColor: "#E3F2FD",
                              color: "#1565C0",
                            }),

                            ...(statusText === "Selected" && {
                              backgroundColor: "#E8F0FE",
                              color: "#0D47A1",
                            }),

                            ...(statusText === "Already Assigned" && {
                              backgroundColor: "#EEEEEE",
                              color: "#616161",
                            }),

                            ...(statusText === "Active in Table" && {
                              backgroundColor: "#dfd8f0",
                              color: "#5d30d0",
                            }),

                            ...(statusText === "Deleted (Inactive)" && {
                              backgroundColor: "#FDECEC",
                              color: "#C62828",
                            }),

                            ...(statusText === "Deleted (Available)" && {
                              backgroundColor: "#FFF8E1",
                              color: "#F57C00",
                            }),
                          }}
                        />

                        {wasPreviouslyRemoved && onUndoEmployee && (
                          <IconButton
                            size="small"
                            title="Undo"
                            onClick={(event) => {
                              event.stopPropagation();
                              onUndoEmployee(emp.id);
                              setSelectedEmployees((previous) =>
                                previous.filter((item) => item.GUID !== emp.GUID)
                              );
                            }}
                            sx={{ ml: 0.5 }}
                          >
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        )}

                        {isAssignedElsewhere && (
                          <IconButton size="small" title="Already assigned" disabled sx={{ ml: 0.5 }}>
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        )}

                      </td>

                    </tr>

                  );

                })

              )}

            </tbody>

          </table>
        </Box>

        {!loading && filteredRows.length > PAGE_SIZE && renderPagination()}

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
          <Box>
            {allTableEmployeeIds.size > 0 && (
              <Chip
                label={`${allTableEmployeeIds.size} employee(s) in table (${inactiveCount} deleted)`}
                size="small"
                color="default"
                sx={{ mr: 1 }}
              />
            )}
            {selectedEmployees.length > 0 && (
              <Chip
                label={`${newEmployeesCount} new employee(s) ready to add`}
                size="small"
                color="primary"
              />
            )}
          </Box>

          <Button
            className="btn"
            variant="contained"
            onClick={handleAddEmployees}
            disabled={newEmployeesCount === 0}
          >
            Add ({newEmployeesCount})
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeLookupDialog;
